import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useSearch } from '../api/queries.js';

import type { SearchResponse } from '@baseline/shared';

// Prototype command palette: Ctrl/⌘+K opens it, arrows move, Enter opens, Esc closes.
// Matches come from `GET /search`, filtered by the caller's visibility (BR-021).

type Item = SearchResponse['items'][number];

const PAGE_PATHS: Record<string, string> = {
  dashboard: '/',
  alerts: '/alerts',
  compare: '/compare',
  connectors: '/connectors',
};

export function pathOf(item: Item): string {
  return item.kind === 'page' ? (PAGE_PATHS[item.page] ?? '/') : `/alerts/${item.alertId}`;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return <div id="pal" hidden />;
  return (
    <div id="pal">
      <PaletteBody onClose={onClose} />
    </div>
  );
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cur, setCur] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const search = useSearch(q);
  const items = q.trim() ? (search.data?.items ?? []) : [];
  const at = Math.min(cur, Math.max(0, items.length - 1));

  useEffect(() => input.current?.focus(), []);

  const run = (i: number) => {
    const item = items[i];
    if (!item) return;
    onClose();
    void navigate(pathOf(item));
  };

  return (
    <>
      <div className="pal-bg" onClick={onClose} />
      <div className="pal" role="dialog" aria-label="Search">
        <input
          ref={input}
          id="pal-q"
          placeholder="Search alerts and pages…"
          value={q}
          autoComplete="off"
          aria-label="Search alerts and pages"
          onChange={(e) => {
            setQ(e.target.value);
            setCur(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setCur(Math.min(items.length - 1, at + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setCur(Math.max(0, at - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              run(at);
            }
          }}
        />
        <div className="pal-l">
          {items.length ? (
            items.map((item, i) => (
              <button
                key={`${item.kind}:${item.label}`}
                type="button"
                className={`pal-i${i === at ? ' on' : ''}`}
                onClick={() => run(i)}
              >
                <span>{item.label}</span>
                <em>{item.kind}</em>
              </button>
            ))
          ) : q && !search.isFetching ? (
            <div className="none" style={{ margin: 12 }}>
              No matches.
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
